import React, {useState, forwardRef, useCallback} from 'react'
import styled from 'styled-components'
import themeGet from '@fuegokit/react/utils/theme-get'
import {Merge} from '@fuegokit/react/utils/polymorphic'
import {CheckboxProps as FuegokitCheckboxPrimitiveProps} from '@fuegokit/react/components/Checkbox'
import sx, {SxProp} from '@fuegokit/react/sx'
import {useRefObjectAsForwardedRef} from '@fuegokit/react/hooks'
import Box, {BoxProps} from '@fuegokit/react/components/Box'
import {AtlassianCheckboxProps, Size} from './types'
import {AtlasCheckboxLabel} from './AtlasCheckboxLabel'
import {AtlasLabelText, RequiredIndicator} from './AtlasLabelText'

import {AtlasCheckboxIcon} from './AtlasCheckboxIcon'
import {UnstyledCheckbox} from './UnstyledCheckbox'

// Exclude 'size' from InputHTMLAttributes
type CheckboxProps = Omit<FuegokitCheckboxPrimitiveProps, 'size'>

type InternalAtlasCheckboxProps = Merge<AtlassianCheckboxProps, CheckboxProps>

export type AtlasCheckboxProps = Exclude<InternalAtlasCheckboxProps, 'indeterminate' | 'required'>

type InternalCheckboxWrapperProps = AtlasCheckboxProps & BoxProps & SxProp

const StyledAtlasCheckbox = styled(UnstyledCheckbox)<AtlasCheckboxProps & SxProp>`
  // resets
  display: grid;
  width: 100%;
  height: 100%;
  margin: 0;
  appearance: none;
  border: none;
  grid-area: 1 / 1 / 2 / 2;
  opacity: 0;
  outline: none;

  & + svg {
    /**
    *  Change the variables --checkbox-background-color; --checkbox-border-color
    *  and --checkbox-tick-color according to user interactions.
    *  All other variables are constant.
    *  All styles from the input target the sibling svg.
    */
    --checkbox-background-color: var(--local-background);
    --checkbox-border-color: var(--local-border);
    --checkbox-tick-color: var(--local-tick-rest);

    color: var(--checkbox-background-color);
    fill: var(--checkbox-tick-color);
    grid-area: 1 / 1 / 2 / 2;
    pointer-events: none;
    transition: color 0.2s ease-in-out, fill 0.2s ease-in-out;

    rect:first-of-type {
      stroke: var(--checkbox-border-color);
      stroke-width: 2;
      transition: stroke 0.2s ease-in-out;
    }
  }
  &:focus + svg,
  &:checked:focus + svg {
    --checkbox-border-color: var(--local-border-focus);
  }
  &:hover + svg {
    --checkbox-background-color: var(--local-background-hover);
    --checkbox-border-color: var(--local-border-hover);
  }
  &:checked:hover + svg {
    --checkbox-background-color: var(--local-background-checked-hover);
    --checkbox-border-color: var(--local-border-checked-hover);
  }
  &:checked + svg {
    --checkbox-background-color: var(--local-background-checked);
    --checkbox-border-color: var(--local-border-checked);
    --checkbox-tick-color: var(--local-tick-checked);
  }
  &[data-invalid] + svg {
    --checkbox-border-color: var(--local-border-invalid);
  }
  &:checked[data-invalid] + svg {
    --checkbox-border-color: var(--local-border-checked-invalid);
  }
  &:active + svg {
    --checkbox-background-color: var(--local-background-active);
    --checkbox-border-color: var(--local-border-active);
  }
  &:checked:active + svg {
    --checkbox-background-color: var(--local-background-active);
    --checkbox-border-color: var(--local-border-active);
    --checkbox-tick-color: var(--local-tick-active);
  }
  &:disabled + svg,
  &:disabled:hover + svg,
  &:disabled:focus + svg,
  &:disabled:active + svg,
  &:disabled[data-invalid] + svg {
    --checkbox-background-color: var(--local-background-disabled);
    --checkbox-border-color: var(--local-border-disabled);
    cursor: not-allowed;
    pointer-events: none;
  }
  &:disabled:checked + svg {
    --checkbox-tick-color: var(--local-tick-disabled);
  }
  @media screen and (forced-colors: active) {
    & + svg {
      --checkbox-background-color: Canvas;
      --checkbox-border-color: CanvasText;
      --checkbox-tick-color: CanvasText;
    }

    &:checked + svg,
    &:checked:hover + svg {
      --checkbox-background-color: Canvas;
      --checkbox-border-color: CanvasText;
      --checkbox-tick-color: CanvasText;
    }

    &:focus + svg rect:first-of-type {
      stroke: 'Highlight';
    }

    &[data-invalid] + svg {
      --checkbox-border-color: 'Highlight';
    }
    &:checked[data-invalid] + svg {
      --checkbox-border-color: 'Highlight';
    }

    &:disabled + svg,
    &:disabled:hover + svg,
    &:disabled:focus + svg,
    &:disabled:active + svg,
    &:disabled[data-invalid] + svg {
      --checkbox-background-color: 'Canvas';
      --checkbox-border-color: 'GrayText';
      --checkbox-tick-color: 'GrayText';
    }
  }

  ${sx}
`

const Wrapper = styled(Box)<InternalCheckboxWrapperProps>`
  display: grid;

  // Atlassian tokens with fallbacks -- when @atlaskit/tokens v1 is stable, this should be safe to restore
  /* --local-background: var(--ds-background-input, #fafbfc);
  --local-background-active: var(--ds-background-input-pressed, #deebff);
  --local-background-checked: var(--ds-background-selected-bold, #0052cc);
  --local-background-checked-hover: var(--ds-background-selected-bold-hovered, #0065ff);
  --local-background-disabled: var(--ds-background-disabled, #f4f5f7);
  --local-background-hover: var(--ds-background-input-hovered, #ebecf0);
  --local-border: var(--ds-border-input, #dfe1e6);
  --local-border-active: var(--ds-border, #deebff);
  --local-border-checked: var(--ds-background-selected-bold, #0052cc);
  --local-border-checked-hover: var(--ds-background-selected-bold-hovered, #0065ff);
  --local-border-checked-invalid: var(--ds-border-danger, #ff5630);
  --local-border-disabled: var(--ds-background-disabled, #f4f5f7);
  --local-border-focus: var(--ds-border-focused, #4c9aff);
  --local-border-hover: var(--ds-border-input, #dfe1e6);
  --local-border-invalid: var(--ds-border-danger, #ff5630);
  --local-tick-active: var(--ds-icon-inverse, #0052cc);
  --local-tick-checked: var(--ds-icon-inverse, #fafbfc);
  --local-tick-disabled: var(--ds-icon-disabled, #a5adba);
  --local-tick-rest: transparent; */

  // Fuegokit tokens
  --local-background: ${themeGet('colors.background.input.default')};
  --local-background-active: ${themeGet('colors.background.input.pressed')};
  --local-background-checked: ${themeGet('colors.background.selected.bold.default')};
  --local-background-checked-hover: ${themeGet('colors.background.selected.bold.hovered')};
  --local-background-disabled: ${themeGet('colors.background.disabled')};
  --local-background-hover: ${themeGet('colors.background.input.hovered')};
  --local-border: ${themeGet('colors.border.input')};
  --local-border-active: ${themeGet('colors.border.default')};
  --local-border-checked: ${themeGet('colors.background.selected.bold.default')};
  --local-border-checked-hover: ${themeGet('colors.background.selected.bold.hovered')};
  --local-border-checked-invalid: ${themeGet('colors.border.danger')};
  --local-border-disabled: ${themeGet('colors.background.disabled')};
  --local-border-focus: ${themeGet('colors.border.focused')};
  --local-border-hover: ${themeGet('colors.border.input')};
  --local-border-invalid: ${themeGet('colors.border.danger')};
  --local-tick-active: ${themeGet('colors.icon.inverse')};
  --local-tick-checked: ${themeGet('colors.icon.inverse')};
  --local-tick-disabled: ${themeGet('colors.icon.disabled')};
  --local-tick-rest: transparent;
`

const AtlasCheckbox = forwardRef((props: AtlasCheckboxProps, forwardedRef) => {
  const innerRef = React.useRef<HTMLInputElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  const {
    isChecked: isCheckedProp,
    isDisabled = false,
    isInvalid = false,
    defaultChecked = false,
    isIndeterminate = false,
    size = 'medium',
    onChange: onChangeProps,
    label,
    labelSize = 'default',
    name,
    value,
    isRequired,
    testId,
    isLabelVisuallyHidden,
    ...rest
  } = props
  const [isCheckedState, setIsCheckedState] = useState(isCheckedProp !== undefined ? isCheckedProp : defaultChecked)
  if (!label) {
    throw new Error(
      'A semantic label is required for the AtlasCheckbox component. Use the isLabelVisuallyHidden prop if your label should be hidden visually.'
    )
  }
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsCheckedState(e.target.checked)
      if (onChangeProps) {
        onChangeProps(e)
      }
    },
    [onChangeProps]
  )

  // Use isChecked from the state if it is controlled
  const isChecked = isCheckedProp === undefined ? isCheckedState : isCheckedProp

  return (
    <Wrapper>
      <AtlasCheckboxLabel label={label as string} labelSize={labelSize} testId={testId || undefined}>
        <StyledAtlasCheckbox
          {...rest}
          type="checkbox"
          ref={innerRef}
          disabled={isDisabled}
          checked={isChecked}
          value={value}
          name={name}
          onChange={onChange}
          required={isRequired}
          aria-checked={isIndeterminate ? 'mixed' : isChecked}
          aria-invalid={isInvalid ? 'true' : undefined}
          data-testid={testId && `${testId}--hidden-checkbox`}
          data-invalid={isInvalid ? 'true' : undefined}
        />
        <AtlasCheckboxIcon
          size={size as Size}
          isIndeterminate={isIndeterminate}
          isChecked={isChecked}
          testId={testId}
        />
        {label && !isLabelVisuallyHidden && (
          <AtlasLabelText labelSize={labelSize} data-testid={testId && `${testId}-checkbox-label`}>
            {label}
            {isRequired && <RequiredIndicator />}
          </AtlasLabelText>
        )}
      </AtlasCheckboxLabel>
    </Wrapper>
  )
})

AtlasCheckbox.displayName = 'AtlasCheckbox'
export default AtlasCheckbox
